import { ReviewFilterPills } from '@components/reviews/components/search-sort-filter/review-filter-pills/review-filter-pills';
import { currentResultsCountAtom } from '@components/reviews/shared-atoms';
import useAnalyticsContext from '@hooks/useAnalyticsContext';
import { useBaseStorefrontAnalytics } from '@hooks/useBaseStorefrontAnalytics';
import { useAppSelector } from '@redux/hooks';
import { Link } from '@tkww/orion-web-ds-action';
import { type InputCommon, Select } from '@tkww/orion-web-ds-input';
import type { ListboxController } from '@tkww/orion-web-lib-listbox';
import { type ClassesRecord, compose } from '@xo-union/react-css-modules';
import { Body1 } from '@xo-union/tk-ui-typography';
import cx from 'classnames';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import React, { useEffect, useMemo, useState } from 'react';
import { AppliedPills } from './applied-pills';
import {
	currentStarRatingsAtom,
	previousStarRatingsAtom,
	searchTextAtom,
	shouldShowTextAtom,
	sortSelectionAtom,
	vendorIdAtom,
} from './atoms';
import { useSearchSortFilterReviews } from './hooks/use-search-sort-filter-reviews';
import { SearchBar } from './search-bar';
import Styles from './styles.scss';

const selectClasses = compose<
	InputCommon.FieldClassNames,
	ClassesRecord<InputCommon.FieldClassNames>
>({
	list: Styles.selectList,
});

export const SearchSortFilter = () => {
	const [sortOpen, setSortOpen] = useState(false);
	const sortSelection = useAtomValue(sortSelectionAtom);
	const vendorId = useAppSelector((state) => state.vendor.vendor?.id);
	const setVendorId = useSetAtom(vendorIdAtom);
	const [currentStarRating, setCurrentStarRatings] = useAtom(
		currentStarRatingsAtom,
	);
	const setPreviousStarRatings = useSetAtom(previousStarRatingsAtom);
	const searchText = useAtomValue(searchTextAtom);
	const reviewCount = useAtomValue(currentResultsCountAtom);

	const {
		clearSearchText,
		clearAll,
		setSearchText,
		setSortSelection,
		displayText,
	} = useSearchSortFilterReviews();
	const shouldShowText = useAtomValue(shouldShowTextAtom);

	const handleSortChange = (event: ListboxController.SelectedChangeEvent) => {
		const newSortSelection = event.detail.value?.toString();
		if (newSortSelection && newSortSelection !== sortSelection) {
			setSortSelection(newSortSelection);
		}
	};

	const handleClearAll = () => {
		clearAll();
		setCurrentStarRatings([]);
		setPreviousStarRatings([]);
	};

	useEffect(() => {
		setVendorId(vendorId || '');
	}, [vendorId, setVendorId]);

	const baseStorefrontAnalytics = useBaseStorefrontAnalytics();
	const { track } = useAnalyticsContext();

	const trackSortOpen = () =>
		track({
			event: 'Review Interaction',
			properties: {
				action: 'open sort',
				...baseStorefrontAnalytics,
			},
		});

	const trackClearAll = () =>
		track({
			event: 'Review Interaction',
			properties: {
				action: 'clear all',
				...baseStorefrontAnalytics,
			},
		});

	const handleClear = () => {
		clearSearchText();
		trackClearAll();
	};

	const handleClose = () => {
		setSortOpen(false);
	};

	const toggleOpen = () => {
		setSortOpen((open) => {
			const newOpenState = !open;
			if (newOpenState) {
				trackSortOpen();
			}
			return newOpenState;
		});
	};

	const reviewCountText = useMemo(() => {
		const pluralization = reviewCount !== 1 ? 's' : '';
		const forSearchTerm =
			!currentStarRating?.length && searchText?.length
				? ` for '${searchText}'`
				: '';
		return `${reviewCount} matching review${pluralization}${forSearchTerm} | `;
	}, [reviewCount, searchText, currentStarRating]);

	return (
		<div>
			<div className={Styles.searchSortContainer}>
				<div className={Styles.searchBarItem}>
					<SearchBar handleClear={handleClear} setSearchText={setSearchText} />
				</div>
				<div className={Styles.sortDropdownItem}>
					<Select
						name="Sort by"
						classes={selectClasses}
						isExpanded={sortOpen}
						selected={sortSelection}
						onSelectedChange={handleSortChange}
						onExpandedChange={toggleOpen}
						onFocusLeave={handleClose}
						options={[
							{
								text: 'Top reviews',
								value: 'selectiontype:asc|createddate:desc',
							},
							{
								text: 'Newest first',
								value: 'createddate:desc',
							},
							{
								text: 'Oldest first',
								value: 'createddate:asc',
							},
							{
								text: 'Highest rated',
								value: 'rating:desc',
							},
							{
								text: 'Lowest rated',
								value: 'rating:asc',
							},
						]}
					/>
				</div>
			</div>
			<div className={Styles.searchSortPillWrapper}>
				<ReviewFilterPills />
				<AppliedPills />
			</div>
			{shouldShowText && (
				<div
					className={cx(Styles.reviewsCountContainer, {
						[Styles.reviewsCountContainerResults]: reviewCount > 0,
					})}
				>
					<div>
						<Body1 className={Styles.reviewsCountText} bold>
							{reviewCountText}
						</Body1>
						<Link
							as="button"
							type="button"
							styleType="primary"
							onClick={handleClearAll}
						>
							Clear search
						</Link>
					</div>
					{displayText && (
						<p className={Styles.reviewsCountText}>{displayText}</p>
					)}
				</div>
			)}
		</div>
	);
};
